import React, { useState, useRef, KeyboardEvent } from 'react';
import { useChatTheme } from '../../contexts/ChatThemeContext';
import { PaperClipIcon, FaceSmileIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';

interface MessageInputProps {
  onSendMessage: (message: string) => void;
  onTyping?: () => void;
  onStopTyping?: () => void;
  placeholder?: string;
  disabled?: boolean;
  showAttachment?: boolean;
  showEmoji?: boolean;
  onAttach?: () => void;
  onEmojiClick?: () => void;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  onTyping,
  onStopTyping,
  placeholder = "Type an SMS message...",
  disabled = false,
  showAttachment = true,
  showEmoji = true,
  onAttach,
  onEmojiClick,
}) => {
  const { theme } = useChatTheme();
  const [message, setMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout>();

  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSendMessage(message.trim());
      setMessage('');
      onStopTyping?.();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);
    
    // Handle typing indicator
    if (e.target.value && onTyping) {
      onTyping();
      
      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Stop typing after 3 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        onStopTyping?.();
      }, 3000);
    } else if (!e.target.value && onStopTyping) {
      onStopTyping();
    }
  };

  const inputWrapperStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    background: isFocused ? theme.colors.inputWrapperFocusBackground : theme.colors.inputWrapperBackground,
    borderRadius: theme.borderRadius.input,
    padding: '8px 8px 8px 20px',
    border: `2px solid ${isFocused ? theme.colors.borderFocus : theme.name === 'dark' ? 'rgba(67, 97, 238, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
    boxShadow: isFocused ? theme.shadows.inputWrapperFocus : theme.shadows.inputWrapper,
    transform: isFocused ? 'translateY(-2px)' : 'translateY(0)',
  };

  const buttonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    padding: '8px',
    cursor: 'pointer',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.colors.mutedText,
  };

  const sendButtonStyle: React.CSSProperties = {
    background: theme.colors.accent,
    border: 'none',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: message.trim() && !disabled ? 'pointer' : 'not-allowed',
    boxShadow: theme.shadows.sendButton,
    opacity: message.trim() && !disabled ? 1 : 0.5,
  };

  return (
    <div style={{
      padding: theme.spacing.inputPadding,
      background: theme.colors.inputBackground,
      borderTop: `1px solid ${theme.colors.borderDefault}`,
      backdropFilter: 'blur(20px)',
      boxShadow: theme.shadows.input,
      position: 'relative',
      zIndex: 10,
    }}>
      <div style={inputWrapperStyle}>
        <input
          ref={inputRef}
          type="text"
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          disabled={disabled}
          style={{
            flex: 1,
            background: 'none',
            border: 'none',
            outline: 'none',
            color: theme.colors.primaryText,
            fontSize: theme.typography.inputFontSize,
            padding: '10px 0',
            fontFamily: theme.typography.fontFamily,
          }}
        />
        
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {showAttachment && (
            <button
              style={buttonStyle}
              onClick={onAttach}
              disabled={disabled}
              title="Attach file"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = theme.name === 'dark' 
                  ? 'rgba(67, 97, 238, 0.15)' 
                  : 'rgba(67, 97, 238, 0.08)';
                e.currentTarget.style.color = theme.colors.accent;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none';
                e.currentTarget.style.color = theme.colors.mutedText;
              }}
            >
              <PaperClipIcon className="h-5 w-5" />
            </button>
          )}
          
          {showEmoji && (
            <button
              style={buttonStyle}
              onClick={onEmojiClick}
              disabled={disabled}
              title="Add emoji"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = theme.name === 'dark' 
                  ? 'rgba(67, 97, 238, 0.15)' 
                  : 'rgba(67, 97, 238, 0.08)';
                e.currentTarget.style.color = theme.colors.accent;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none';
                e.currentTarget.style.color = theme.colors.mutedText;
              }}
            >
              <FaceSmileIcon className="h-5 w-5" />
            </button>
          )}
          
          <button
            style={sendButtonStyle}
            onClick={handleSend}
            disabled={!message.trim() || disabled}
            title="Send message"
            onMouseEnter={(e) => {
              if (message.trim() && !disabled) {
                e.currentTarget.style.background = theme.colors.accentHover;
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = theme.shadows.sendButtonHover;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = theme.colors.accent;
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = theme.shadows.sendButton;
            }}
            onMouseDown={(e) => {
              if (message.trim() && !disabled) {
                e.currentTarget.style.transform = 'scale(0.92)';
                e.currentTarget.style.boxShadow = theme.shadows.sendButtonActive;
              }
            }}
            onMouseUp={(e) => {
              if (message.trim() && !disabled) {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = theme.shadows.sendButtonHover;
              }
            }}
          >
            <PaperAirplaneIcon 
              className="h-5 w-5 text-white" 
              style={{ transform: 'translateX(1px)' }}
            />
          </button>
        </div>
      </div>
    </div>
  );
};